import { createContext, useContext, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { getCookie, setCookie, clearCookies } from '../utils/cookies'

const AuthContext = createContext()

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export const AuthProvider = ({ children }) => {
    const location = useLocation()
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [authCode, setAuthCode] = useState(null)
    const [userInfo, setUserInfo] = useState({ name: '', email: '', role: '' })
    const [isLoading, setIsLoading] = useState(true)
    const [isProcessingCallback, setIsProcessingCallback] = useState(true)

    // Extract user information from cookies
    const getUserInfo = () => {
        // Get individual user info cookies
        const userNameFromCookie = getCookie('USER_NAME')
        const userEmailFromCookie = getCookie('USER_EMAIL')
        const userRolesFromCookie = getCookie('USER_ROLES')

        let userName = 'User'
        if (userNameFromCookie) {
            userName = decodeURIComponent(userNameFromCookie.replace(/\+/g, ' '));
        }

        let userEmail = ''
        if (userEmailFromCookie) {
            userEmail = userEmailFromCookie
        }

        // Extract and process roles (pick first role from array)
        let userRole = ''
        if (userRolesFromCookie) {
            try {
                // Parse as JSON array
                const rolesArray = JSON.parse(userRolesFromCookie)
                // Check if it's an array and has at least one element
                if (Array.isArray(rolesArray) && rolesArray.length > 0) {
                    userRole = String(rolesArray[0]) // Convert to string and take first role
                }
            } catch (e) {
                console.warn('Failed to parse user roles:', e)
            }
        }

        return {
            name: userName,
            email: userEmail,
            role: userRole,
        }
    }

    useEffect(() => {
        let authToken = getCookie('AUTH_TOKEN');
        if (authToken) {
            setAuthCode(authToken)
            setIsAuthenticated(true)
            setUserInfo(getUserInfo())
            setIsLoading(false)
            setIsProcessingCallback(false)
            return;
        }

        const urlParams = new URLSearchParams(location.search);
        authToken = urlParams.get('auth_token');

        if(authToken) {
            setIsProcessingCallback(true)
            setIsLoading(true)
            setCookie('AUTH_TOKEN', authToken);
            setCookie('USER_NAME', urlParams.get('name') || '');
            setCookie('USER_EMAIL', urlParams.get('email') || '');
            setCookie('USER_ROLES', urlParams.get('roles') || '[]');
            setCookie('USER_CLIENT_KEY', urlParams.get('client_key') || '');
            // Clean up URL to remove auth parameters and reload
            const newUrl = window.location.origin + window.location.pathname;
            top.location.href = newUrl;
            return;
        }
        const code = urlParams.get('code')

        if (code) {
            setIsProcessingCallback(true)
            setIsLoading(true)
            // Get original path from state parameter
            const state = urlParams.get('state');
            const originalPath = state ? decodeURIComponent(state) : '';
            const callbackUrl = `${window.location.origin}${originalPath}`;
            console.log(`${process.env.REACT_APP_BACKEND_DIRECT_URL}/callback.fxl?code=${code}&codeCallbackUrl=${encodeURIComponent(callbackUrl)}`)
            // Redirect to backend callback
            window.location.href = `${process.env.REACT_APP_BACKEND_DIRECT_URL}/callback.fxl?code=${code}&codeCallbackUrl=${encodeURIComponent(callbackUrl)}`
            return
        }

        setIsAuthenticated(false)
        setAuthCode(null)
        setUserInfo({ name: '', email: '', role: '' })
        setIsLoading(false)

        // Check if current path is a public route
        const currentPath = window.location.pathname;
        const publicRoutes = ['/terms'];
        const isPublicRoute = publicRoutes.includes(currentPath);

        // Only redirect to login if not on a public route
        if (!isPublicRoute) {
            // Small delay before redirect to show message
            setTimeout(() => {
                const domain = process.env.REACT_APP_AUTH0_DOMAIN;
                const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
                const redirectUri = encodeURIComponent(window.location.origin);
                // Store current path in state parameter to preserve it after auth
                const state = encodeURIComponent(currentPath);
                const authUrl = `https://${domain}/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${encodeURI('openid profile email')}&state=${state}`;

                window.location.href = authUrl;
            }, 100)
        }
        setIsProcessingCallback(false)
    }, [location.search])

    const logout = () => {
        // Clear authentication state
        setIsAuthenticated(false)
        setAuthCode(null)
        setUserInfo({ name: '', email: '', role: '' })

        // Clear all authentication-related cookies and tour cookies
        const cookiesToClear = ['AUTH_TOKEN', 'USER_ID', 'USER_NAME', 'USER_EMAIL', 'USER_ROLES', 'USER_CLIENT_KEY', 'navbarTourSeen', 'trendsTourSeen', 'dietRequestsTourSeen']
        clearCookies(cookiesToClear)


        // Logout from Auth0 and redirect back to the app
        const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN
        const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID
        const returnUrl = encodeURIComponent(window.location.origin)

        const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${auth0ClientId}&returnTo=${returnUrl}`

        // Redirect to Auth0 logout which will then redirect back to our app
        window.location.href = logoutUrl
    }

    const value = {
        isAuthenticated,
        authCode,
        userInfo,
        logout,
        setCookie,
        getCookie,
        isLoading,
        isProcessingCallback,
    }

    // Show loading states for unauthenticated users with navigation bar
    if (!isAuthenticated) {
        return (
            <AuthContext.Provider value={value}>
                {children}
            </AuthContext.Provider>
        )
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Clear only authentication cookies (preserve tour/onboarding preferences)
export const clearAuthCookies = () => {
    const authCookiesToClear = ['AUTH_TOKEN', 'USER_ID', 'USER_NAME', 'USER_EMAIL', 'USER_ROLES', 'USER_CLIENT_KEY']
    clearCookies(authCookiesToClear)
}

// Standalone logout function for use outside of React components (e.g., in api interceptors)
export const logoutUser = () => {
    // Clear all authentication-related cookies and tour cookies
    const cookiesToClear = ['AUTH_TOKEN', 'USER_ID', 'USER_NAME', 'USER_EMAIL', 'USER_ROLES', 'USER_CLIENT_KEY', 'navbarTourSeen', 'trendsTourSeen', 'dietRequestsTourSeen']
    clearCookies(cookiesToClear)

    // Logout from Auth0 and redirect back to the app
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN
    const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID
    const returnUrl = encodeURIComponent(window.location.origin)

    const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${auth0ClientId}&returnTo=${returnUrl}`

    // Redirect to Auth0 logout which will then redirect back to our app
    window.location.href = logoutUrl
}
